import { ApplicationRef, EnvironmentInjector, Injectable, createComponent, inject } from '@angular/core';
import { GovcoConfirmModalComponent } from '../../shared/govco/govco-confirm-modal/govco-confirm-modal.component';
import { GovcoAlertModalComponent } from '../../shared/govco/govco-alert-modal/govco-alert-modal.component';

/** Uses the existing GOV.CO dialogs, including keyboard focus and restoration. */
@Injectable({ providedIn: 'root' })
export class ProjectDialogService {
  private readonly app = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private queue: Promise<unknown> = Promise.resolve();
  private confirming = false;

  confirm(message: string): Promise<boolean> {
    if (this.confirming) return Promise.resolve(false);
    this.confirming = true;
    return this.enqueue(() => this.open(message, false)).finally(() => { this.confirming = false; });
  }

  success(message: string): Promise<boolean> {
    return this.enqueue(() => this.open(message, true));
  }

  private enqueue(action: () => Promise<boolean>): Promise<boolean> {
    const result = this.queue.then(action);
    this.queue = result.catch(() => false);
    return result;
  }

  private open(message: string, success: boolean): Promise<boolean> {
    const previous = document.activeElement as HTMLElement | null;
    const host = document.createElement('div');
    document.body.appendChild(host);
    const ref = success
      ? createComponent(GovcoAlertModalComponent, { hostElement: host, environmentInjector: this.environmentInjector })
      : createComponent(GovcoConfirmModalComponent, { hostElement: host, environmentInjector: this.environmentInjector });
    ref.setInput('open', true);
    ref.setInput('message', message);
    ref.setInput('title', success ? 'Éxito' : 'Confirmar acción');
    if (success) ref.setInput('variant', 'success');
    else { ref.setInput('confirmLabel', 'Sí, continuar'); ref.setInput('cancelLabel', 'Cancelar'); }
    this.app.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return new Promise(resolve => {
      let finished = false;
      const close = (value: boolean) => {
        if (finished) return;
        finished = true;
        document.removeEventListener('keydown', keydown, true);
        this.app.detachView(ref.hostView);
        ref.destroy(); host.remove();
        document.body.style.overflow = previousOverflow;
        if (previous?.isConnected) previous.focus();
        resolve(value);
      };
      const keydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(false); }
        if (event.key === 'Tab') {
          const buttons = Array.from(host.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
          if (!buttons.length) return;
          const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
          event.preventDefault();
          buttons[(current + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
        }
      };
      ref.instance.closed.subscribe(() => close(success));
      if (ref.instance instanceof GovcoConfirmModalComponent) ref.instance.confirmed.subscribe(() => close(true));
      document.addEventListener('keydown', keydown, true);
      host.querySelector<HTMLButtonElement>('button')?.focus();
    });
  }
}
